/**
 * A doula, as the staff supervising her see her.
 *
 * Three components, because a supervisor arrives with one of three questions and
 * only one of them at a time: has her card been issued, has she been paid, and
 * what has she delivered. They load independently so one failing does not blank
 * the other two.
 *
 * SHE AND HER SUPERVISOR SEE THE SAME CARD — last four and network, never the
 * full number. A staff screen that revealed more than the cardholder's own would
 * be the wrong way round.
 *
 * HER TWO PAYMENT STREAMS ARE NEVER SUMMED. A claim is billed to a payer for a
 * visit; a grant incentive comes out of a foundation's money. Different
 * lifecycles, different dispute routes, and a combined total answers no question
 * anyone actually asks. They share a list and are told apart by a chip.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CreditCard, Loader2 } from 'lucide-react';

interface StaffDoulaCard {
  card_id: number;
  last4: string;
  network: string | null;
  card_type: string | null;
  status: string;
  current_balance: number | null;
  available_balance: number | null;
  issued_on: string | null;
  expires_on: string | null;
}

interface StaffDoula {
  panel_id: number;
  display_name: string;
  npi: string | null;
  status: string;
  accepting_new_clients: boolean;
  caseload_ceiling: number | null;
  active_clients: number;
  card: StaffDoulaCard | null;
}

interface StaffDoulaPayment {
  source: string;
  reference: string | null;
  amount: number | null;
  status: string;
  service_date: string | null;
  paid_on: string | null;
  for_member: string | null;
  what_for: string | null;
  method: string | null;
  is_held: boolean;
  held_reason: string | null;
}

interface StaffDoulaActivity {
  encounter_date: string;
  kind: string;
  minutes: number | null;
  for_member: string | null;
  documented: boolean;
  exclusion_code: string | null;
}

function money(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toFixed(2)}`;
}

function when(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

/** Loads one thing, says so while it does, and reports its own failure. */
function useOne<T>(url: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await authFetchJson<T>(url));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load');
    } finally {
      setLoading(false);
    }
  }, [url]);
  useEffect(() => {
    void load();
  }, [load]);
  return { data, loading, error };
}

const Block = ({
  title,
  loading,
  error,
  children,
}: {
  title: string;
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
}) => (
  <section className="rounded-md border p-4">
    <h3 className="mb-3 text-sm font-semibold">{title}</h3>
    {loading ? (
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    ) : error ? (
      <p className="flex items-start gap-2 text-sm text-red-700">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </p>
    ) : (
      children
    )}
  </section>
);

export function StaffDoulaCardBlock({ panelId }: { panelId: number }) {
  const { t } = useTranslation();
  const { data, loading, error } = useOne<StaffDoula>(`/api/doulas/${panelId}`);
  const card = data?.card ?? null;

  return (
    <Block title={t('staffDoula.card', 'Card')} loading={loading} error={error}>
      {!card ? (
        /* No card is ORDINARY — most doulas are paid by transfer. Said plainly
           so nobody chases a card that was never meant to exist. */
        <p className="text-sm text-muted-foreground">
          {t('staffDoula.noCard', 'No card issued. She is paid by transfer.')}
        </p>
      ) : (
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
          <span className="flex items-center gap-2 font-mono text-base">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            {card.network ? `${card.network} ` : ''}•••• {card.last4}
          </span>
          <Badge variant={card.status === 'ACTIVE' ? 'success' : 'secondary'}>{card.status}</Badge>
          {card.available_balance !== null && (
            <span className="text-sm">
              <span className="text-muted-foreground">{t('staffDoula.available', 'Available')} </span>
              <span className="font-medium tabular-nums">{money(card.available_balance)}</span>
            </span>
          )}
          {card.expires_on && (
            <span className="text-sm text-muted-foreground">
              {t('staffDoula.expires', 'expires {{date}}', { date: when(card.expires_on) })}
            </span>
          )}
        </div>
      )}
    </Block>
  );
}

export function StaffDoulaPaymentsBlock({ panelId }: { panelId: number }) {
  const { t } = useTranslation();
  const { data, loading, error } = useOne<StaffDoulaPayment[]>(`/api/doulas/${panelId}/payments`);
  const rows = data ?? [];

  return (
    <Block title={t('staffDoula.payments', 'Payment history')} loading={loading} error={error}>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('staffDoula.noPayments', 'Nothing has been paid to her yet.')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-left">
              <tr>
                <th className="py-2 pr-3 font-medium">{t('staffDoula.source', 'Source')}</th>
                <th className="py-2 pr-3 font-medium">{t('staffDoula.forWhat', 'For')}</th>
                <th className="py-2 pr-3 text-right font-medium">{t('staffDoula.amount', 'Amount')}</th>
                <th className="py-2 pr-3 font-medium">{t('staffDoula.date', 'Date')}</th>
                <th className="py-2 font-medium">{t('staffDoula.status', 'Status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={`${p.source}-${p.reference ?? i}`} className="border-b last:border-0 align-top">
                  <td className="py-2 pr-3">
                    {/* The two streams are labelled, never added together. */}
                    <Badge variant="secondary">
                      {p.source === 'claim'
                        ? t('staffDoula.claim', 'Claim')
                        : t('staffDoula.grant', 'Grant')}
                    </Badge>
                    {p.reference && (
                      <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
                        {p.reference}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">
                    {p.what_for || p.for_member || '—'}
                    {p.method && (
                      <span className="mt-0.5 block text-xs capitalize">
                        {p.method.replace(/_/g, ' ').toLowerCase()}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{money(p.amount)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">
                    {when(p.paid_on || p.service_date)}
                  </td>
                  <td className="py-2">
                    <Badge variant={p.is_held ? 'warning' : p.paid_on ? 'success' : 'secondary'}>
                      {p.is_held ? t('staffDoula.held', 'On hold') : p.status}
                    </Badge>
                    {p.is_held && p.held_reason && (
                      <span className="mt-0.5 block text-xs text-amber-800">{p.held_reason}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Block>
  );
}

export function StaffDoulaActivityBlock({ panelId }: { panelId: number }) {
  const { t } = useTranslation();
  const { data, loading, error } = useOne<StaffDoulaActivity[]>(`/api/doulas/${panelId}/activity`);
  const rows = data ?? [];

  return (
    <Block title={t('staffDoula.activity', 'Work completed')} loading={loading} error={error}>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('staffDoula.noActivity', 'No visits recorded yet.')}
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((a, i) => (
            <li key={`${a.encounter_date}-${i}`} className="flex flex-wrap items-baseline gap-x-3 text-sm">
              <span className="w-28 shrink-0 text-muted-foreground">{when(a.encounter_date)}</span>
              <span className="font-medium">
                {t(`staffDoula.kind.${a.kind}`, a.kind.replace(/_/g, ' '))}
              </span>
              {a.for_member && <span className="text-muted-foreground">{a.for_member}</span>}
              {a.minutes !== null && (
                <span className="text-muted-foreground">
                  {t('staffDoula.minutes', '{{count}} min', { count: a.minutes })}
                </span>
              )}
              {!a.documented && (
                <Badge variant="warning">{t('staffDoula.undocumented', 'No notes')}</Badge>
              )}
              {/* Care deliberately not billed is still care she gave. */}
              {a.exclusion_code && (
                <Badge variant="secondary">
                  {t('staffDoula.notBilled', 'not billed')}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </Block>
  );
}

/** All three, for a staff screen showing one doula. */
/**
 * How the programme reaches her.
 *
 * This is what makes the fan-out real. 24 of 25 contracted doulas have no
 * account, so without a number or an address there is no channel to send an
 * offer on — the ask is recorded, never delivered, and waits silently. The
 * panel carried no contact column at all until TRUS-679.
 */
export function StaffDoulaContactBlock({ panelId }: { panelId: number }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [channel, setChannel] = useState('');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await authFetchJson<{ email: string | null; phone: string | null; preferred_channel: string | null }>(
          `/api/doulas/${panelId}`,
        );
        if (cancelled) return;
        setEmail(d.email || '');
        setPhone(d.phone || '');
        setChannel(d.preferred_channel || '');
      } catch {
        /* The detail block above already reports a failed load; a second
           identical error would just be noise. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [panelId]);

  const save = async () => {
    setSaving(true);
    setNote(null);
    try {
      await authFetchJson(`/api/doulas/${panelId}/contact`, {
        method: 'PUT',
        body: JSON.stringify({ email: email || null, phone: phone || null, preferred_channel: channel || null }),
      });
      setNote(t('doula.contactSaved', 'Saved. She can be sent an offer now.'));
    } catch (e) {
      setNote(e instanceof Error ? e.message : t('doula.contactFailed', 'We could not save that.'));
    } finally {
      setSaving(false);
    }
  };

  const reachable = Boolean(email || phone);

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="font-medium">{t('doula.howToReachHer', 'How to reach her')}</h3>
        {!reachable && (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            {t('doula.cannotBeAsked', 'cannot be sent an offer')}
          </Badge>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        <input
          className="rounded-md border px-3 py-2 text-sm"
          placeholder={t('doula.email', 'Email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          className="rounded-md border px-3 py-2 text-sm"
          placeholder={t('doula.phone', 'Phone')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <select
          className="rounded-md border px-3 py-2 text-sm"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
        >
          <option value="">{t('doula.noPreference', 'No preference')}</option>
          <option value="sms">{t('doula.sms', 'Text message')}</option>
          <option value="email">{t('doula.emailChannel', 'Email')}</option>
          <option value="in_app">{t('doula.inApp', 'In the app')}</option>
          <option value="phone">{t('doula.phoneCall', 'Phone call')}</option>
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          className="rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          onClick={() => void save()}
          disabled={saving}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('doula.save', 'Save')}
        </button>
        {note && <span className="text-sm text-muted-foreground">{note}</span>}
      </div>
    </div>
  );
}

export default function StaffDoulaPanel({ panelId }: { panelId: number }) {
  return (
    <div className="flex flex-col gap-4">
      <StaffDoulaContactBlock panelId={panelId} />
      <StaffDoulaCardBlock panelId={panelId} />
      <StaffDoulaPaymentsBlock panelId={panelId} />
      <StaffDoulaActivityBlock panelId={panelId} />
    </div>
  );
}
